import { AccountCurrencyCode } from '@lune-climate/lune'
import { stableJson, useErrorSnackbar } from '@lune-fe/lune-components-lib'
import {
    ButtonNew,
    currenciesList,
    LabeledFormSection,
    LuneThemeNew,
    LuneThemeProvider,
    Modal,
    SelectNew,
    Text,
} from '@lune-fe/lune-ui-lib'
import AddIcon from '@mui/icons-material/Add'
import { Box } from '@mui/material'
import Stack from '@mui/material/Stack'
import { FormApi } from 'final-form'
import { FC, useRef, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { Field, FieldRenderProps, Form } from 'react-final-form'

import { FormInputNew } from 'components/FormInputNew'
import { createClientAccount } from 'endpoints/api'
import { SnackbarMessages } from 'SnackbarMessages'
import { isAccountCurrencyCode } from 'views/CalculateEmissions/RFQ/utils/utils'

interface ModalProps {
    modalOpen: boolean
    onModalClose: (accountCreated: boolean) => void
}

const FormCurrencyPicker = ({ input: { onChange, value } }: FieldRenderProps<string>) => (
    <SelectNew
        placeholder={'Select a currency'}
        items={currenciesList}
        value={value}
        onChange={(e) => {
            if (isAccountCurrencyCode(e)) {
                onChange(e)
            } else {
                throw new Error(
                    `Programming Error. Unexpected account currency option selected: ${e}`,
                )
            }
        }}
    />
)

enum NewAccountForm {
    NEW_CLIENT_ACCOUNT_NAME = 'newClientAccountName',
    NEW_CLIENT_ACCOUNT_CURRENCY = 'newClientAccountCurrency',
}

interface NewAccountFormProps {
    [NewAccountForm.NEW_CLIENT_ACCOUNT_NAME]: string
    [NewAccountForm.NEW_CLIENT_ACCOUNT_CURRENCY]: AccountCurrencyCode
}

const NewClientAccountModal = ({ modalOpen, onModalClose }: ModalProps) => {
    const [creatingAccount, setCreatingAccount] = useState<boolean>(false)
    const [formValues, setFormValues] = useState<Partial<NewAccountFormProps>>({})
    const formApiRef = useRef<FormApi<NewAccountFormProps> | null>(null)
    const previousValuesStrRef = useRef<string>('')
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()

    const onSubmit = async (formValues: NewAccountFormProps) => {
        setCreatingAccount(true)
        const result = await createClientAccount({
            name: formValues[NewAccountForm.NEW_CLIENT_ACCOUNT_NAME],
            currency: formValues[NewAccountForm.NEW_CLIENT_ACCOUNT_CURRENCY],
        })
        if (result.isOk()) {
            snackbar('Client account created successfully')
            onModalClose(true)
        } else {
            snackbar(SnackbarMessages.GENERIC_ERROR, {
                variant: 'error',
                error: result.error,
            })
            setCreatingAccount(false)
        }
    }

    const isFormValid = Boolean(
        formValues[NewAccountForm.NEW_CLIENT_ACCOUNT_NAME]?.trim() &&
            formValues[NewAccountForm.NEW_CLIENT_ACCOUNT_CURRENCY],
    )

    return (
        <LuneThemeProvider theme={LuneThemeNew}>
            <Modal
                maxWidth={'sm'}
                fullWidth
                open={modalOpen}
                onClose={() => onModalClose(false)}
                header={<Text variant={'h5'}>Create new client account</Text>}
                actions={
                    <>
                        <ButtonNew
                            sx={{ minWidth: '130px' }}
                            disabled={creatingAccount}
                            variant={'secondary'}
                            onClick={() => onModalClose(false)}
                        >
                            Cancel
                        </ButtonNew>
                        <ButtonNew
                            disabled={creatingAccount || !isFormValid}
                            onClick={() => formApiRef.current?.submit()}
                        >
                            Create account
                        </ButtonNew>
                    </>
                }
            >
                <Box
                    sx={{
                        width: '100%',
                    }}
                >
                    <Form
                        onSubmit={onSubmit}
                        initialValues={{
                            [NewAccountForm.NEW_CLIENT_ACCOUNT_NAME]: undefined,
                            [NewAccountForm.NEW_CLIENT_ACCOUNT_CURRENCY]: undefined,
                        }}
                        render={({ form, values }) => {
                            formApiRef.current = form

                            // Only update state if values actually changed to prevent infinite loop
                            const valuesStr = JSON.stringify(values)
                            if (stableJson(valuesStr) !== stableJson(values)) {
                                previousValuesStrRef.current = valuesStr
                                setFormValues(values)
                            }

                            return (
                                <Stack
                                    direction="column"
                                    sx={{
                                        width: '100%',
                                    }}
                                    spacing={{ xs: 4 }}
                                >
                                    <LabeledFormSection nested={true} title={'Client account name'}>
                                        <Field
                                            name={NewAccountForm.NEW_CLIENT_ACCOUNT_NAME}
                                            component={FormInputNew}
                                            label="Account Name"
                                        />
                                    </LabeledFormSection>
                                    <LabeledFormSection
                                        nested={true}
                                        title={'Currency'}
                                        subTile={
                                            'Used for offset quoting and pricing. Cannot be changed after account creation'
                                        }
                                        sx={{
                                            width: '100%',
                                            '.MuiInputBase-root': {
                                                width: '100%',
                                            },
                                        }}
                                    >
                                        <Field
                                            name={NewAccountForm.NEW_CLIENT_ACCOUNT_CURRENCY}
                                            component={FormCurrencyPicker}
                                            label="Currency"
                                        />
                                    </LabeledFormSection>
                                </Stack>
                            )
                        }}
                    />
                </Box>
            </Modal>
        </LuneThemeProvider>
    )
}

export default NewClientAccountModal

export const NewClientAccountButton: FC<{
    onModalClose: (accountCreated: boolean) => void
}> = ({ onModalClose }) => {
    return (
        <ButtonNew
            leadingIcon={<AddIcon />}
            onClick={() => {
                openNewClientAccountModal().then((res) => onModalClose(res))
            }}
        >
            New Client Account
        </ButtonNew>
    )
}

export const openNewClientAccountModal = (): Promise<boolean> => {
    return new Promise((resolve) => {
        const container = document.createElement('div')
        document.body.appendChild(container)
        const root = ReactDOM.createRoot(container)

        const handleClose = (accountCreated: boolean) => {
            resolve(accountCreated)
            setTimeout(() => {
                root.unmount()
                container.remove()
            }, 0)
        }

        root.render(<NewClientAccountModal onModalClose={handleClose} modalOpen={true} />)
    })
}
